import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FiArrowLeft, FiThumbsUp, FiStar, FiMapPin, FiDollarSign, FiClock, FiCopy, FiCheck, FiEdit2 } from 'react-icons/fi'
import { RootState } from '@/store'
import { successStoriesApi } from '@/services/api/successStories'
import { SuccessStory } from '@/types/successStory'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import Avatar from '@/components/ui/Avatar'

const SuccessStoryDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user: currentUser, isAuthenticated } = useSelector((state: RootState) => state.auth)

    const [story, setStory] = useState<SuccessStory | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isHelpful, setIsHelpful] = useState(false)
    const [copied, setCopied] = useState(false)
    const [helpfulLoading, setHelpfulLoading] = useState(false)

    useEffect(() => {
        const fetchStory = async () => {
            if (!id) {
                setError('Story ID not found')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)
                const response = await successStoriesApi.getSuccessStoryById(id)
                if (response.data?.story) {
                    setStory(response.data.story)
                } else {
                    setError('Story not found')
                }
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load success story')
                console.error('Error fetching story:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchStory()
    }, [id])

    const handleMarkHelpful = async () => {
        if (!isAuthenticated) {
            navigate('/login')
            return
        }

        if (!id || isHelpful || helpfulLoading) return

        try {
            setHelpfulLoading(true)
            await successStoriesApi.markStoryHelpful(id)
            setIsHelpful(true)
            if (story) {
                setStory({ ...story, helpful: story.helpful + 1 })
            }
        } catch (err) {
            console.error('Error marking story as helpful:', err)
        } finally {
            setHelpfulLoading(false)
        }
    }

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    const isStoryOwner = () => {
        if (!isAuthenticated || !currentUser || !story) return false

        // Handle both populated user object and user ID string
        const storyUserId = typeof story.user === 'string' ? story.user : story.user?._id
        return currentUser._id === storyUserId
    }

    const handleEdit = () => {
        if (story) {
            navigate(`/success-stories/${story._id}/edit`)
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <LoadingSpinner />
            </div>
        )
    }

    if (error || !story) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
                    <button
                        onClick={() => navigate('/success-stories')}
                        className="flex items-center text-primary-600 hover:text-primary-700 mb-6 sm:mb-8 text-sm sm:text-base"
                    >
                        <FiArrowLeft className="w-4 h-4 mr-2" />
                        Back to Stories
                    </button>
                    <div className="text-center">
                        <p className="text-sm sm:text-body text-red-600 dark:text-red-400 mb-4 sm:mb-6">
                            {error || 'Story not found'}
                        </p>
                        <button
                            onClick={() => navigate('/success-stories')}
                            className="px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm sm:text-base font-medium"
                        >
                            View All Stories
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const categoryColors: Record<string, { bg: string; text: string }> = {
        technology: { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-800 dark:text-blue-200' },
        creative: { bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-800 dark:text-purple-200' },
        marketing: { bg: 'bg-orange-100 dark:bg-orange-900', text: 'text-orange-800 dark:text-orange-200' },
        other: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-200' }
    }

    const colors = categoryColors[story.category] || categoryColors.other

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/success-stories')}
                    className="flex items-center text-primary-600 hover:text-primary-700 mb-6 sm:mb-8 font-medium text-sm sm:text-base"
                >
                    <FiArrowLeft className="w-4 h-4 mr-2" />
                    Back to Stories
                </button>

                {/* Main Content */}
                <article className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-sm md:shadow-lg">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 md:gap-6 lg:gap-8 mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-200 dark:border-gray-700">
                        <Avatar
                            avatarId={story.avatar}
                            alt={story.name}
                            size="2xl"
                            className="flex-shrink-0"
                            fallbackIcon={
                                <div className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-xl sm:text-2xl font-heading font-bold">
                                    {story.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </div>
                            }
                        />

                        <div className="flex-1 min-w-0">
                            <div className="mb-2 sm:mb-3">
                                <span className={`inline-block px-2 sm:px-3 py-1 ${colors.bg} ${colors.text} text-xs sm:text-ui-sm font-semibold rounded-full capitalize`}>
                                    {story.category}
                                </span>
                                {story.featured && (
                                    <span className="ml-2 inline-block px-2 sm:px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs sm:text-ui-sm font-semibold rounded-full">
                                        ⭐ Featured
                                    </span>
                                )}
                            </div>
                            <h1 className="text-2xl sm:text-3xl md:text-4xl heading-1 text-gray-900 dark:text-white mb-2 sm:mb-3">{story.title}</h1>
                            <div className="flex flex-col xs:flex-row xs:flex-wrap gap-2 xs:gap-3 sm:gap-4 md:gap-6 text-xs sm:text-ui text-gray-600 dark:text-gray-400">
                                <div className="flex items-center">
                                    <span className="font-medium text-gray-900 dark:text-white">{story.name}</span>
                                </div>
                                <div className="flex items-center">
                                    <span className="font-medium">{story.role}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <FiMapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                    <span className="truncate">{story.location}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8 p-4 sm:p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                            <div className="flex items-center gap-1 text-xs sm:text-ui text-gray-600 dark:text-gray-400 mb-1 sm:mb-2">
                                <FiStar className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                                <span className="truncate">Rating</span>
                            </div>
                            <div className="text-lg sm:text-2xl heading-card text-gray-900 dark:text-white">{story.rating.toFixed(1)}</div>
                        </div>
                        <div>
                            <div className="text-xs sm:text-ui text-gray-600 dark:text-gray-400 mb-1 sm:mb-2 truncate">Projects</div>
                            <div className="text-lg sm:text-2xl heading-card text-gray-900 dark:text-white">{story.projectsCompleted}</div>
                        </div>
                        <div>
                            <div className="flex items-center gap-1 text-xs sm:text-ui text-gray-600 dark:text-gray-400 mb-1 sm:mb-2">
                                <FiDollarSign className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                <span className="truncate">Earnings</span>
                            </div>
                            <div className="text-lg sm:text-2xl heading-card text-success-600 dark:text-success-400 truncate">{story.totalEarnings}</div>
                        </div>
                        <div>
                            <div className="flex items-center gap-1 text-xs sm:text-ui text-gray-600 dark:text-gray-400 mb-1 sm:mb-2">
                                <FiClock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                <span className="truncate">Joined</span>
                            </div>
                            <div className="text-lg sm:text-2xl heading-card text-gray-900 dark:text-white truncate">{story.joinDate}</div>
                        </div>
                    </div>

                    {/* Achievement */}
                    <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900 dark:to-primary-800 border-2 border-primary-200 dark:border-primary-700 rounded-lg">
                        <h2 className="text-base sm:text-lg font-heading font-bold text-primary-900 dark:text-primary-100">
                            🏆 {story.achievement}
                        </h2>
                    </div>

                    {/* Main Story */}
                    <div className="mb-6 sm:mb-8">
                        <h2 className="text-xl sm:text-2xl font-heading font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Their Journey</h2>
                        <p className="text-sm sm:text-body text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                            {story.story}
                        </p>
                    </div>

                    {/* Testimonial */}
                    <div className="mb-6 sm:mb-8 p-4 sm:p-6 border-l-4 border-primary-600 bg-gray-50 dark:bg-gray-700 rounded">
                        <p className="text-sm sm:text-body italic text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                            "{story.testimonial}"
                        </p>
                        <p className="text-xs sm:text-ui text-gray-600 dark:text-gray-400">
                            — {story.name}
                        </p>
                    </div>

                    {/* Skills */}
                    <div className="mb-6 sm:mb-8">
                        <h3 className="text-base sm:text-lg font-heading font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Key Skills</h3>
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                            {story.skills.map((skill, index) => (
                                <span
                                    key={index}
                                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs sm:text-ui font-medium rounded-full whitespace-nowrap"
                                >
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Engagement Section */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 sm:py-6 border-t border-gray-200 dark:border-gray-700 gap-4 sm:gap-6">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-ui text-gray-600 dark:text-gray-400">
                            <span className="whitespace-nowrap">{story.views} views</span>
                            <span className="hidden sm:inline">•</span>
                            <span className="whitespace-nowrap">{story.helpful} found helpful</span>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-start sm:justify-end">
                            {isStoryOwner() && (
                                <button
                                    onClick={handleEdit}
                                    className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-xs sm:text-sm"
                                >
                                    <FiEdit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span className="hidden sm:inline">Edit Story</span>
                                    <span className="sm:hidden">Edit</span>
                                </button>
                            )}

                            <button
                                onClick={handleCopyLink}
                                className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-xs sm:text-sm font-medium"
                            >
                                {copied ? (
                                    <>
                                        <FiCheck className="w-3 h-3 sm:w-4 sm:h-4" />
                                        <span className="hidden sm:inline">Copied</span>
                                    </>
                                ) : (
                                    <>
                                        <FiCopy className="w-3 h-3 sm:w-4 sm:h-4" />
                                        <span className="hidden sm:inline">Share</span>
                                    </>
                                )}
                            </button>

                            <button
                                onClick={handleMarkHelpful}
                                disabled={isHelpful || helpfulLoading}
                                className={`flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors font-medium text-xs sm:text-sm ${isHelpful
                                    ? 'bg-success-100 dark:bg-success-900 text-success-700 dark:text-success-300 cursor-not-allowed'
                                    : 'bg-primary-600 hover:bg-primary-700 text-white'
                                    }`}
                            >
                                <FiThumbsUp className={`w-3 h-3 sm:w-4 sm:h-4 ${isHelpful ? 'fill-current' : ''}`} />
                                <span className="hidden sm:inline">{isHelpful ? 'Helpful' : 'Mark Helpful'}</span>
                                <span className="sm:hidden">{isHelpful ? 'Done' : 'Helpful'}</span>
                            </button>
                        </div>
                    </div>
                </article>

                {/* Related Stories Section - Optional */}
                <div className="mt-8 sm:mt-12 pt-8 sm:pt-12 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-xl sm:text-2xl font-heading font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">More Stories</h3>
                    <button
                        onClick={() => navigate('/success-stories')}
                        className="px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors text-sm sm:text-base"
                    >
                        Browse All Stories
                    </button>
                </div>
            </div>
        </div>
    )
}

export default SuccessStoryDetailPage